import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { Marathon } from '@/api/courses';
import { usersApi, type AdminUser, type Enrollment } from '@/api/users';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { useAuthStore } from '@/stores/auth';
import { canTransfer, enrollmentStatusKey } from '@/lib/enrollmentActions';
import { transferSources } from '@/lib/enrollmentTransfer';
import { TransferDialog } from './TransferDialog';

/**
 * Курсы ученика в шапке карточки (решение владельца 28.09.2026): курс, срок, статус записи, «основной», куратор — и
 * кнопка «Перенести на другой курс». До этого курсы жили только в выпадающем списке блока «Доступ к курсам» внизу
 * страницы, со статусом сырым словом `active`.
 *
 * Куратор записи — кураторы её группы; у записи без группы — личный куратор (`curator_name` учётки, LEGACY).
 */
export function StudentEnrollmentsSummary({
  user,
  enrollments,
  marathons,
}: {
  user: AdminUser;
  enrollments: Enrollment[];
  marathons: Marathon[];
}) {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const byId = new Map(marathons.map((m) => [m.id, m]));
  const withGroup = [...new Set(enrollments.filter((e) => e.group_id).map((e) => e.marathon_id))];
  const groupQueries = useQueries({
    queries: withGroup.map((mid) => ({
      queryKey: ['admin', 'marathon', mid, 'groups'],
      queryFn: () => usersApi.listGroups(mid),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const groups = new Map(groupQueries.flatMap((q) => q.data?.items ?? []).map((g) => [g.id, g]));
  // Личный куратор записи без группы — по его id: `curator_name` карточка одного пользователя не отдаёт (только
  // список), и без этого у личного ученика стояло бы «не назначен» (стенд 28.09.2026, user1017).
  const personalIds = [...new Set(enrollments.filter((e) => !e.group_id && e.curator_id).map((e) => e.curator_id!))];
  const curatorQueries = useQueries({
    queries: personalIds.map((cid) => ({
      queryKey: ['admin', 'student', cid],
      queryFn: () => usersApi.get(cid),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const curatorNames = new Map(curatorQueries.flatMap((q) => (q.data ? [[q.data.id, q.data.full_name] as const] : [])));

  // Активные сверху, остальные — по кнопке: у ученика с историей переносов снятых записей бывает больше живых.
  const sorted = [...enrollments].sort(
    (a, b) => Number(b.status === 'active') - Number(a.status === 'active') || Number(!!b.is_primary) - Number(!!a.is_primary),
  );
  const active = sorted.filter((e) => e.status === 'active');
  const shown = showAll ? sorted : active;
  const transferable = canTransfer(role, user.status) && transferSources(marathons, enrollments).length > 0;
  const date = (s?: string) => (s ? new Date(s).toLocaleDateString('ru-RU') : '—');

  const curatorOf = (e: Enrollment) => {
    if (e.group_id) {
      const g = groups.get(e.group_id);
      // Группы курса отдаются только admin: у менеджера — просто «в группе», а не вечное «загружается».
      if (!g) return groupQueries.some((q) => q.isLoading) ? t('adminStudentHead.groupLoading') : t('adminStudentHead.inGroup');
      return `${g.name} · ${g.curators.map((c) => c.name).join(', ') || t('adminDetail.groupNoCurators')}`;
    }
    if (!e.curator_id) return t('adminDetail.noCurator');
    return curatorNames.get(e.curator_id) ?? user.curator_name ?? t('adminStudentHead.personalCurator');
  };

  return (
    <div className="mt-4 w-full">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-label text-on-surface-variant">{t('adminStudentHead.coursesTitle')}</p>
        {transferable && (
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            <span className="material-symbols-outlined">sync_alt</span>
            {t('adminTransfer.button')}
          </Button>
        )}
      </div>
      {shown.length === 0 ? (
        <p className="mt-2 text-sm text-on-surface-variant">{t('adminStudentHead.noActive')}</p>
      ) : (
        <ul className="mt-2 divide-y divide-outline-variant/40">
          {shown.map((e) => {
            const statusKey = enrollmentStatusKey(e.status);
            return (
              <li key={e.id} className="py-1.5 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{byId.get(e.marathon_id)?.title ?? e.marathon_id.slice(0, 8)}</span>
                  <Pill variant={e.status === 'active' ? 'success' : 'neutral'}>{statusKey ? t(statusKey) : e.status}</Pill>
                  {e.is_primary && <Pill variant="primary">{t('adminStudentHead.primary')}</Pill>}
                </div>
                <p className="mt-0.5 text-xs text-on-surface-variant">
                  {t('adminStudentHead.period', { from: date(e.access_from), until: date(e.access_until) })} ·{' '}
                  {t('adminStudentHead.curator')}: {curatorOf(e)}
                </p>
              </li>
            );
          })}
        </ul>
      )}
      {sorted.length > active.length && (
        <button type="button" className="mt-2 text-xs text-primary hover:underline" onClick={() => setShowAll((v) => !v)}>
          {showAll ? t('adminStudentHead.hideInactive') : t('adminStudentHead.showInactive', { count: sorted.length - active.length })}
        </button>
      )}
      {open && (
        <TransferDialog
          open={open}
          onClose={() => setOpen(false)}
          studentId={user.id}
          marathons={marathons}
          enrollments={enrollments}
        />
      )}
    </div>
  );
}
